import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Handles the OAuth callback from Emergent's managed Google Auth.
 * Captures #session_id=xxx from URL hash, exchanges it with backend, logs user in.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      console.log("[GoogleOAuth] Processing callback, hash:", hash);

      // Check for session_id (primary) or session_token (fallback)
      const match = hash.match(/session_id=([^&]+)/) || hash.match(/session_token=([^&]+)/);

      if (!match) {
        console.error("[GoogleOAuth] No session_id found in hash");
        navigate("/login", { replace: true });
        return;
      }

      const sessionId = decodeURIComponent(match[1]);
      console.log("[GoogleOAuth] Found session_id, exchanging...");

      // Retry logic - session might not be immediately available
      let lastError = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const { data } = await axios.post(
            `${API}/auth/google/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );

          console.log("[GoogleOAuth] Session exchange successful:", data.email);

          // Store token (same as email/password login)
          if (data.token) {
            localStorage.setItem("appmi_token", data.token);
          }

          // Clear the hash from URL to prevent re-processing
          if (window.history.replaceState) {
            window.history.replaceState(null, "", window.location.pathname);
          }

          // Refresh auth state then navigate
          await checkAuth();
          navigate("/dashboard", { replace: true });
          return;
        } catch (err) {
          lastError = err;
          console.warn(`[GoogleOAuth] Attempt ${attempt}/3 failed:`, err.response?.status, err.response?.data?.detail || err.message);
          if (attempt < 3) {
            // Wait before retry (session might need time to propagate)
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }
        }
      }

      console.error("[GoogleOAuth] All attempts failed:", lastError?.response?.data || lastError?.message);
      navigate("/login?error=oauth_failed", { replace: true });
    };

    processSession();
  }, [navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center" data-testid="auth-callback-loading">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground/70 text-sm">Completing sign in...</p>
      </div>
    </div>
  );
}
